import _ from 'lodash'
import moment from 'moment';
import store from '@/store'
import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators'
import {$axios} from '@/util/axios'
import {FormType, Request, Response, Conversation, UserType, Output} from '@/types/asix';
import {AppModule} from '@/store/modules/app';

export interface AsixState {
  uuid: string
  latestId: number
  request: Request[]
  response: Response[]
}

@Module({
  name: 'asix',
  namespaced: true,
  dynamic: true,
  store,
})
export default class Asix extends VuexModule {
  public uuid: string = '';
  public latestId: number = 0;
  public request: Request[] = [];
  public response: Response[] = [];
  public appStore = AppModule
  public MSG_WAIT_TIME: number = 1.5 * 1000;

  get conversations(): Conversation[] {
    const res: Conversation[] = []
    this.response.map((r: Response) => {
      res.push({
        id: r.id,
        userType: UserType.ChatBot,
        outList: r.output,
      })
    })
    this.request.map((r: Request, index: number) => {
      res.push({
        id: r.id,
        userType: UserType.User,
        outList: [{
          expression_form: FormType.Text,
          text: r.text,
          content_attribute: '',
          file_id: null,
          action_class: '',
        }],
      })
    })
    return _.orderBy(res, 'id', 'asc')
  }

  get botTimeLine(): Conversation[] {
    return this.conversations.filter((t: Conversation) => t.userType === UserType.ChatBot)
  }

  get latestBotConv(): Conversation | null {
    return this.botTimeLine.length > 0 ? _.orderBy(this.botTimeLine, 'id', 'desc')[0] : null
  }

  @Mutation
  public setRequest(req: Request) {
    this.request.push(req)
  }

  @Mutation
  public async setResponse(resList: Response[]) {
    for (const res of resList) {
      this.latestId = res.id
      this.response.push(res)
      await new Promise((resolve) => setTimeout(resolve, this.MSG_WAIT_TIME));
    }
  }

  @Mutation
  public setUuid(uuid: string) {
    this.uuid = uuid
  }

  @Mutation
  public clearState() {
    this.uuid = ''
    this.latestId = 0
    this.request = []
    this.response = []
  }

  @Action({rawError: true})
  public async post(text: string): Promise<void> {
    try {
      const req: Request = {
        id: moment().valueOf(),
        uuid: this.uuid,
        text,
      }
      this.setRequest(req)
      const {data} = await $axios.post('', req)

      const resList: Response[] = [];
      const outputsList = makeOutput(data.output)
      for (const outputs of outputsList) {
        const res: Response = {
          id: moment().valueOf(),
          uuid: data.uuid,
          output: outputs,
        }
        resList.push(res);
      }
      this.setResponse(resList)
    } catch (e) {
      this.appStore.setIsMaintenance(true)
    }
  }

  @Action({rawError: true})
  public async listPost(item: Output): Promise<void> {
    const req: Request = {
      id: moment().valueOf(),
      uuid: this.uuid,
      text: item.text,
    }
    this.setRequest(req)
    // ボタン押下時、textではなくcontent_attributeをAIHubに送信
    const {data} = await $axios.post('', {
      id: moment().valueOf(),
      uuid: this.uuid,
      text: item.content_attribute,
    })

    const resList: Response[] = [];
    const outputsList = makeOutput(data.output)
    for (const outputs of outputsList) {
      const res: Response = {
        id: moment().valueOf(),
        uuid: data.uuid,
        output: outputs,
      }
      resList.push(res);
    }
    this.setResponse(resList)
  }

  @Action({rawError: true})
  public async welcomePost(): Promise<void> {
    const text = 'WEBUI発話開始'
    const req = {
      text,
    }
    const {data} = await $axios.post('', req)
    const resList: Response[] = [];
    const outputsList = makeOutput(data.output)
    for (const outputs of outputsList) {
      const res: Response = {
        id: moment().valueOf(),
        uuid: data.uuid,
        output: outputs,
      }
      resList.push(res);
    }
    this.clearState()
    this.setUuid(data.uuid)
    this.setResponse(resList)
  }

  // 会話をリセット
  @Action({rawError: true})
  public async resetConvPost(): Promise<void> {
      const text = 'はじめから質問しなおす'
      const req = {
        id: moment().valueOf(),
        uuid: this.uuid,
        text,
      }
      this.setRequest(req)
      const {data} = await $axios.post('', {
        id: moment().valueOf(),
        uuid: this.uuid,
        text: '_WelcomeMessage',
      })
      const resList: Response[] = [];
      const outputsList = makeOutput(data.output)
      for (const outputs of outputsList) {
        const res: Response = {
          id: moment().valueOf(),
          uuid: data.uuid,
          output: outputs,
        }
        resList.push(res);
      }
      this.setUuid(data.uuid)
      this.setResponse(resList)
  }
}


const makeOutput = (output: any[]): Output[][] => {
  // AIHUBが例外エラー時には、文字列の配列が返ってくるので、型を合わせる
  const strItems = output.filter((out: any) => typeof out === 'string')
  if (strItems.length > 0) {
    const outputs: Output[][] = [];
    strItems.map((strItem: string) => {
      const outputElements = [];
      const [headingOutputs, excludedHeadingText] = makeHeadTextOutput(strItem)
      const [imgOutputs, excludedImgText] = makeImgOutput(excludedHeadingText)
      const [btnOutputs, excludedBtnText] = makeBtnOutput(excludedImgText)
      const [linkOutputs, excludedLinkText] = makeLinkOutput(excludedBtnText)
      headingOutputs.map((headOutput: Output) => outputElements.push(headOutput))
      outputElements.push({
        expression_form: FormType.Text,
        text: _.trim(excludedLinkText),
        content_attribute: _.trim(excludedLinkText),
        file_id: null,
        action_class: '',
      })
      imgOutputs.map((imgOutput: Output) => outputElements.push(imgOutput))
      btnOutputs.map((btnOutput: Output) => outputElements.push(btnOutput))
      linkOutputs.map((linkOutput: Output) => outputElements.push(linkOutput))
      outputs.push(outputElements)
    })
    return outputs
  } else {
    return output
  }
}

const makeHeadTextOutput = (text: string): [Output[], string] => {
  const headOutputs: Output[] = []
  const headingTexts = text.match(/<h>(?: .+?)?.*?<\/h>/g)
  if (headingTexts) {
    headingTexts.map((headStr: string) => {
      text = _.replace(text, headStr, '')
      const outputText = _.trim(_.trim(headStr, '</h>'), '<h>')
      headOutputs.push({
        expression_form: FormType.HeadText,
        text: outputText,
        content_attribute: outputText,
        file_id: null,
        action_class: '',
      })
    })
  }
  return [headOutputs, text]
}

const makeImgOutput = (text: string): [Output[], string] => {
  const imgOutputs: Output[] = []
  const imgTexts = text.match(/<img>(?: .+?)?.*?<\/img>/g)
  if (imgTexts) {
    imgTexts.map((imgStr: string) => {
      text = _.replace(text, imgStr, '')
      const outputText = _.replace(_.replace(imgStr, '</img>', ''), '<img>', '')
      imgOutputs.push({
        expression_form: FormType.ImgText,
        text: outputText,
        content_attribute: outputText,
        file_id: null,
        action_class: '',
      })
    })
  }
  return [imgOutputs, text]
}

const makeBtnOutput = (text: string): [Output[], string] => {
  const btnOutputs: Output[] = []
  const btnTexts = text.match(/\[\[(?: .+?)?.*?]]/g)
  if (btnTexts) {
    btnTexts.map((btnStr: string) => {
      text = _.replace(text, btnStr, '')
      const [btnText, btnContentAttribute] = _.split(btnStr, '||')
      btnOutputs.push({
        expression_form: FormType.Button,
        text: _.trim(btnText, '[['),
        content_attribute: _.trim(btnContentAttribute, ']]'),
        file_id: null,
        action_class: '',
      })
    })
  }
  return [btnOutputs, text]
}

const makeLinkOutput = (text: string): [Output[], string] => {
  const linkOutputs: Output[] = []
  const linkTexts = text.match(/<link>(?: .+?)?.*?<\/link>/g)
  if (linkTexts) {
    linkTexts.map((linkStr: string) => {
      text = _.replace(text, linkStr, '')
      const [linkText, linkContentAttribute] = _.split(linkStr, '<linkto>')
      // textとcontent_attributeの_.trimを_.replaceに変更。
      linkOutputs.push({
        expression_form: FormType.Link,
        text: _.replace(linkText, '<link>', ''),
        content_attribute: _.replace(linkContentAttribute, '</link>', ''),
        file_id: null,
        action_class: '',
      })
    })
  }
  return [linkOutputs, text]
}

export const AsixModule = getModule(Asix)
